/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState, useMemo, useRef } from "react";

// react-router-dom components
import { useLocation, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Badge from "@mui/material/Badge";
import NotificationsIcon from "@mui/icons-material/Notifications";
import List from "@mui/material/List";
import { useTheme } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 2 PRO React examples
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavItem from "examples/Sidenav/SidenavItem";
import SidenavList from "examples/Sidenav/SidenavList";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import { logoutSystem, postNoResponse, get, post } from "utils/WebService";

// Material Dashboard 2 PRO React context
import {
  setMiniSidenav,
  setDarkMode,
  useMaterialUIController
} from "context";
import { useUser } from "context/UserContext";

function Sidenav({ color,
  routes,
  onPageSelect,
  ...rest }) {
  const [openCollapse, setOpenCollapse] = useState(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState(false);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const items = pathname.split("/").slice(1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];



  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  } else if (!darkMode) {
    // If it's a dark gradient background in light mode
    textColor = "white";
  }

  const { userId, userName, userFoto, setUserFoto, userFunctionalities, userPerfils } = useUser();

  const isOperador = userPerfils?.some(p => 
    p?.tipoPerfil === 5 ||
    p?.nome?.toLowerCase().includes('opera') || 
    p?.nome?.toLowerCase().includes('backoffice') ||
    (typeof p === 'string' && (p.toLowerCase().includes('opera') || p.toLowerCase().includes('backoffice')))
  );

  const isAdministrador = userPerfils?.some(p => 
    p?.nome?.toLowerCase().includes('admin') || 
    (typeof p === 'string' && p.toLowerCase().includes('admin'))
  );

  const theme = useTheme();
  const { palette } = theme;
  const { transparent, gradients, white, background } = palette;

  let backgroundValue = darkMode
    ? background.sidenav
    : theme.functions.linearGradient(gradients.dark.main, gradients.dark.state);

  if (color) {
    if (color.startsWith("#")) {
      backgroundValue = color;
    } else if (gradients[color]) {
      backgroundValue = theme.functions.linearGradient(gradients[color].main, gradients[color].state);
    }
  }

  if (transparentSidenav) {
    backgroundValue = transparent.main;
  } else if (whiteSidenav) {
    backgroundValue = white.main;
  }

  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [avatarMenuAnchor, setAvatarMenuAnchor] = useState(null);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);

  const userInitials = useMemo(() => {
    if (!userName) return "U";
    const parts = userName.trim().split(" ").filter(Boolean);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, [userName]);

  const formattedUserName = useMemo(() => {
    if (!userName) return "Usuário";
    const parts = userName.trim().split(" ").filter(Boolean);
    if (parts.length <= 2) return userName.trim();
    return `${parts[0]} ${parts[parts.length - 1]}`;
  }, [userName]);

  const handleAvatarClick = (e) => {
    e.stopPropagation();
    setAvatarMenuAnchor(e.currentTarget);
  };

  const handleCloseAvatarMenu = () => {
    setAvatarMenuAnchor(null);
  };

  const handleOpenPhotoPicker = () => {
    handleCloseAvatarMenu();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemovePhoto = () => {
    handleCloseAvatarMenu();
    if (typeof setUserFoto === "function") {
      setUserFoto(null);
    }
    localStorage.removeItem("userFoto");
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const rawBase64 = event.target?.result;
        if (rawBase64) {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement("canvas");
            const targetSize = 400; // Crisp high-res avatar
            canvas.width = targetSize;
            canvas.height = targetSize;
            const ctx = canvas.getContext("2d");
            if (ctx) {
              const minDim = Math.min(img.width, img.height);
              const sx = (img.width - minDim) / 2;
              const sy = (img.height - minDim) / 2;
              ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, targetSize, targetSize);
              const compressedBase64 = canvas.toDataURL("image/jpeg", 0.88);
              if (typeof setUserFoto === "function") {
                setUserFoto(compressedBase64);
              }
              try {
                localStorage.setItem("userFoto", compressedBase64);
              } catch (err) {
                console.error("Storage quota error:", err);
              }
            }
          };
          img.src = rawBase64;
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDarkMode = () => setDarkMode(dispatch, !darkMode);

  const handleSumit = () => {
    handleDarkMode();

    const body = {
      id: userId,
      temaDark: !darkMode
    };
    sessionStorage.setItem('tema', !darkMode);
    postNoResponse("/users/save-theme", body)
      .then(() => {
      });
  };

  function logout() {
    logoutSystem();
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  const [pausaState, setPausaState] = useState({ emPausa: false, dataInicioPausa: null, tempoTotalPausa: 0 });
  const [currentPauseTime, setCurrentPauseTime] = useState(0);

  const fetchPausaState = () => {
    if (sessionStorage.getItem('user')) {
      get(`/users/pausa?t=${new Date().getTime()}`).then(res => setPausaState(res)).catch(err => console.log(err));
    }
  };

  useEffect(() => {
    setOpenCollapse(collapseName);
    setOpenNestedCollapse(itemParentName);
    fetchPausaState();
    
    const handlePausaToggled = (e) => {
      if (e.detail && typeof e.detail.emPausa !== 'undefined') {
        setPausaState(e.detail);
      } else {
        fetchPausaState();
      }
    };
    window.addEventListener("pausaToggled", handlePausaToggled);
    return () => window.removeEventListener("pausaToggled", handlePausaToggled);
  }, []);

  useEffect(() => {
    let interval;
    if (pausaState.emPausa && pausaState.dataInicioPausa) {
      interval = setInterval(() => {
        const start = new Date(pausaState.dataInicioPausa).getTime();
        const now = new Date().getTime();
        const elapsed = Math.floor((now - start) / 1000);
        setCurrentPauseTime(elapsed);
      }, 1000);
    } else {
      setCurrentPauseTime(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [pausaState]);

  const [pausaMenuAnchor, setPausaMenuAnchor] = useState(null);

  const handlePausaClick = (event) => {
    if (pausaState.emPausa) {
      handleTogglePausa(null);
    } else {
      setPausaMenuAnchor(event.currentTarget);
    }
  };

  const handleTogglePausa = (motivo) => {
    setPausaMenuAnchor(null);
    postNoResponse("/users/pausa", { motivo: motivo || null })
      .then((res) => {
        if (res && res.emPausa !== undefined) {
          setPausaState(res);
          window.dispatchEvent(new CustomEvent("pausaToggled", { detail: res }));
        } else {
          fetchPausaState();
          window.dispatchEvent(new Event("pausaToggled"));
        }
      })
      .catch(e => console.error(e));
  };

  const formatPauseTime = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${h > 0 ? h + 'h ' : ''}${m}m ${s}s`;
  };

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    window.addEventListener("resize", handleMiniSidenav);

    handleMiniSidenav();

    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const renderNestedCollapse = (collapse) => {
    const template = collapse.map(item => {
      const { name, route, key, href, icon } = item;
      return href ? (
        <SidenavItem key={key}
          onClick={() => handleSelectPage(item)}
          name={name}
          icon={icon}
          nested />
      ) : (
        <SidenavItem key={key}
          onClick={() => handleSelectPage(item)}
          name={name}
          active={route === pathname}
          icon={icon}
          nested />
      )
    }
    );

    return template;
  };

  const handleSelectPage = (item) => {
    onPageSelect(item);
    if (window.innerWidth < 1200) {
      setMiniSidenav(dispatch, true);
    }
  };

  const renderCollapse = (collapses) =>
    collapses.map(item => {
      if (item.adminOnly && !isAdministrador) return null;
      const { name, collapse, route, href, key, icon } = item;
      let returnValue;

      if (collapse) {
        returnValue = (
          <SidenavItem
            key={key}
            icon={icon}
            color={color}
            name={name}
            active={key === itemParentName ? "isParent" : false}
            open={openNestedCollapse === key}
            onClick={({ currentTarget }) =>
              openNestedCollapse === key && currentTarget.classList.contains("MuiListItem-root")
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(key)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}>
            <SidenavItem
              color={color}
              name={name}
              active={key === itemName} />
          </Link>
        ) : (
          <SidenavItem
            onClick={() => handleSelectPage(item)}
            key={key}
            color={color}
            name={name}
            icon={icon}
            active={key === itemName} />
        );
      }
      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });

  const renderRoutes = routes.map(
    (item) => {
      const { type, name, icon, title, collapse, noCollapse, key, href, route } = item;
      const hasPermission = key === "username" || (item.menu ? userFunctionalities?.includes(item.menu) : true);
      let returnValue;

      if (type === "collapse") {
        if (href) {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
            />
          );
        } else if (noCollapse && route) {
          if (key == "username") {
            const userAvatarNode = (
              <MDBox
                onClick={handleAvatarClick}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": { transform: "scale(1.08)" }
                }}
                title="Clique para opções da foto"
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  style={{ display: "none" }}
                />
                {userFoto ? (
                  <MDAvatar
                    src={userFoto}
                    alt={userName}
                    size="sm"
                    shadow="md"
                    sx={{
                      width: "36px !important",
                      height: "36px !important",
                      border: "2px solid rgba(255, 255, 255, 0.8)",
                      boxShadow: "0 3px 8px rgba(0,0,0,0.3)",
                      "& img": { objectFit: "cover !important" }
                    }}
                  />
                ) : (
                  <MDAvatar
                    size="sm"
                    shadow="md"
                    sx={{
                      width: "36px !important",
                      height: "36px !important",
                      bgcolor: "info.main",
                      color: "#ffffff !important",
                      fontWeight: "bold",
                      fontSize: "14px",
                      border: "2px solid rgba(255, 255, 255, 0.8)",
                      boxShadow: "0 3px 8px rgba(0,0,0,0.3)"
                    }}
                  >
                    {userInitials}
                  </MDAvatar>
                )}
              </MDBox>
            );

            returnValue = (
              <SidenavCollapse
                key={key}
                name={formattedUserName}
                action={
                  <MDBox 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="center"
                    sx={{ flexShrink: 0, ml: 1 }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSumit();
                      }}
                      title={darkMode ? "Modo Claro" : "Modo Escuro"}
                      sx={{
                        p: 0.5,
                        color: "inherit !important",
                        display: miniSidenav ? "none" : "flex",
                        opacity: miniSidenav ? 0 : 1,
                        transition: "transform 0.2s ease",
                        "&:hover": {
                          transform: "scale(1.15)",
                          bgcolor: darkMode ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.15)"
                        }
                      }}
                    >
                      <Icon fontSize="small" sx={{ fontSize: '18px !important' }}>
                        {darkMode ? "light_mode" : "dark_mode"}
                      </Icon>
                    </IconButton>
                  </MDBox>
                }
                icon={userAvatarNode}
                onClick={() => {
                  if (hasPermission) {
                    handleSelectPage(item);
                  }
                }}
                noCollapse={noCollapse}
                active={key === collapseName}
                sx={{
                  opacity: hasPermission ? 1 : 0.5,
                  cursor: hasPermission ? "pointer" : "not-allowed",
                }}
              />
            );
          } else {
            returnValue = (
              <SidenavCollapse
                key={key}
                name={name}
                icon={icon}

                onClick={() => handleSelectPage(item)}
                noCollapse={noCollapse}
                active={key === collapseName} />
            );
          }

        } else {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              open={openCollapse === key}
              noCollapse={false}
              onClick={() => (openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key))}
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            sx={{ my: 1 }}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }

      return returnValue;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant={window.innerWidth < 1200 ? "temporary" : "permanent"}
      open={!miniSidenav}
      onClose={() => setMiniSidenav(dispatch, true)}
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode, primaryColor: color }}
      sx={{
        overflow: 'visible',
      }}
    >
      <MDBox pt={2} px={2} textAlign="center">
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDBox
            display={{ xs: "block", lg: "none" }}
            position="absolute"
            top={0}
            right={0}
            p={1.625}
            onClick={() => setMiniSidenav(dispatch, !miniSidenav)}
            sx={{ cursor: "pointer" }}
          >
            <MDTypography variant="h6" color="secondary">
              <Icon sx={{ fontWeight: "bold" }}>menu</Icon>
            </MDTypography>
          </MDBox>
          {miniSidenav && (
            <MDBox width="100%" display="flex" justifyContent="center">
              <Icon
                fontSize="small"
                sx={{ color: `${textColor} !important`, cursor: "pointer", fontSize: '18px !important', ml: 0 }}
                onClick={handleSumit}
              >
                {darkMode ? "light_mode" : "dark_mode"}
              </Icon>
            </MDBox>
          )}
        </MDBox>
      </MDBox>
      <Divider
        sx={{ my: 1 }}
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />

      <List
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": {
            width: "5px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: ({ palette: { grey } }) => grey[700],
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: ({ palette: { grey } }) => grey[500],
          }
        }}
      >
        {renderRoutes}
      </List>

      {/* Pause Button and Logout Button at the bottom */}
      <MDBox p={2} mt="auto">
        {isOperador && (
          <MDBox mb={1} display="flex" flexDirection="column" alignItems="center">
            <MDButton
              variant={pausaState.emPausa ? "contained" : "gradient"}
              color={pausaState.emPausa ? "warning" : "info"}
              fullWidth
              onClick={handlePausaClick}
              sx={{ px: miniSidenav ? 0 : 2, display: "flex", justifyContent: miniSidenav ? "center" : "space-between" }}
            >
              <Icon sx={{ mr: miniSidenav ? 0 : 0.5 }}>
                {pausaState.emPausa ? "play_circle" : "pause_circle"}
              </Icon>
              {!miniSidenav && (pausaState.emPausa ? (pausaState.motivo ? `VOLTAR (${pausaState.motivo})` : "VOLTAR") : "PAUSAR")}
              
              {!miniSidenav && pausaState.emPausa && currentPauseTime > 0 && (
                <MDTypography variant="caption" color="white" fontWeight="bold" sx={{ ml: 1, fontSize: "11px" }}>
                  {formatPauseTime(currentPauseTime)}
                </MDTypography>
              )}
            </MDButton>
            {miniSidenav && pausaState.emPausa && currentPauseTime > 0 && (
              <MDTypography variant="caption" color="text" fontWeight="bold" sx={{ fontSize: "9px", mt: 0.5 }}>
                {formatPauseTime(currentPauseTime)}
              </MDTypography>
            )}
          </MDBox>
        )}
        
        <Menu
          anchorEl={pausaMenuAnchor}
          open={Boolean(pausaMenuAnchor)}
          onClose={() => setPausaMenuAnchor(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <MenuItem onClick={() => handleTogglePausa("Banheiro")}>
            <Icon sx={{ mr: 1, fontSize: "20px" }}>wc</Icon> Banheiro
          </MenuItem>
          <MenuItem onClick={() => handleTogglePausa("Almoço")}>
            <Icon sx={{ mr: 1, fontSize: "20px" }}>restaurant</Icon> Almoço
          </MenuItem>
          <MenuItem onClick={() => handleTogglePausa("Café")}>
            <Icon sx={{ mr: 1, fontSize: "20px" }}>local_cafe</Icon> Café / Lanche
          </MenuItem>
          <MenuItem onClick={() => handleTogglePausa("Reunião")}>
            <Icon sx={{ mr: 1, fontSize: "20px" }}>groups</Icon> Reunião
          </MenuItem>
          <MenuItem onClick={() => handleTogglePausa("Treinamento")}>
            <Icon sx={{ mr: 1, fontSize: "20px" }}>school</Icon> Treinamento
          </MenuItem>
          <MenuItem onClick={() => handleTogglePausa("Feedback")}>
            <Icon sx={{ mr: 1, fontSize: "20px" }}>record_voice_over</Icon> Feedback
          </MenuItem>
          <MenuItem onClick={() => handleTogglePausa("Particular")}>
            <Icon sx={{ mr: 1, fontSize: "20px" }}>coffee</Icon> Particular
          </MenuItem>
        </Menu>

        <MDBox
          component="div"
          onClick={logout}
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: miniSidenav ? "center" : "flex-start",
            padding: "0.5rem",
            borderRadius: "0.5rem",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)"
            },
            marginLeft: miniSidenav ? 0 : 0.5,
          }}
        >
          <Icon fontSize="medium" sx={{ color: `${textColor} !important` }}>logout</Icon>
          {!miniSidenav && (
            <MDTypography variant="button" fontWeight="medium" color={textColor} sx={{ ml: 1 }}>
              Sair
            </MDTypography>
          )}
        </MDBox>
      </MDBox>

      {/* Sidebar Toggle Arrow */}
      <MDBox
        sx={{
          position: "absolute",
          top: "50%",
          right: "-12px",
          transform: "translateY(-50%)",
          zIndex: 9999,
          cursor: "pointer",
          background: backgroundValue,
          borderRadius: "50%",
          boxShadow: 3,
          width: "24px",
          height: "24px",
          display: { xs: "none", lg: "flex" },
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid",
          borderColor: "rgba(0,0,0,0.1)",
        }}
        onClick={() => {
          setMiniSidenav(dispatch, !miniSidenav);
          if (!miniSidenav) {
            setOpenCollapse(false);
            setOpenNestedCollapse(false);
          }
        }}
      >
        <Icon sx={{ color: `${textColor} !important`, fontSize: "1rem !important" }}>
          {miniSidenav ? "chevron_right" : "chevron_left"}
        </Icon>
      </MDBox>

      {/* Menu de Opções da Foto do Usuário */}
      <Menu
        anchorEl={avatarMenuAnchor}
        open={Boolean(avatarMenuAnchor)}
        onClose={handleCloseAvatarMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {userFoto && (
          <MenuItem onClick={() => { handleCloseAvatarMenu(); setOpenPreviewModal(true); }}>
            <Icon sx={{ mr: 1 }}>visibility</Icon> Visualizar Foto
          </MenuItem>
        )}
        <MenuItem onClick={handleOpenPhotoPicker}>
          <Icon sx={{ mr: 1 }}>photo_camera</Icon> {userFoto ? "Alterar Foto" : "Adicionar Foto"}
        </MenuItem>
        {userFoto && (
          <MenuItem onClick={handleRemovePhoto} sx={{ color: "error.main" }}>
            <Icon sx={{ mr: 1, color: "error.main" }}>delete</Icon> Remover Foto
          </MenuItem>
        )}
      </Menu>

      {/* Modal de Visualização da Foto Ampliada */}
      {userFoto && (
        <Dialog
          open={openPreviewModal}
          onClose={() => setOpenPreviewModal(false)}
          maxWidth="sm"
        >
          <MDBox p={3} textAlign="center" position="relative" sx={{ minWidth: "300px" }}>
            <IconButton
              onClick={() => setOpenPreviewModal(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <Icon>close</Icon>
            </IconButton>
            <MDTypography variant="h6" mb={2}>
              {userName}
            </MDTypography>
            <MDBox
              component="img"
              src={userFoto}
              alt={userName}
              sx={{
                width: "100%",
                maxWidth: "380px",
                maxHeight: "450px",
                objectFit: "cover",
                borderRadius: "50%",
                border: "4px solid #fff",
                boxShadow: "0 8px 24px rgba(0,0,0,0.3)"
              }}
            />
          </MDBox>
        </Dialog>
      )}

    </SidenavRoot>
  );
}


// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  // brand: PropTypes.string,
  // brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;